import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Redirect, withRouter } from 'react-router-dom';
import { graphql } from 'react-apollo';
import compose from 'lodash.flowright';
import { connect } from 'react-redux';
import qs from 'query-string';

import {
  getWebGalleryRootId,
  hasWebGalleryContributorAccess,
  hasWebGalleryViewAccess,
  requiresAuthentication,
  requiresEmailConfirmation,
  webGalleryHasPassword,
  webGalleryIsInviteOnly,
} from '../utilities/web-galleries';
import { isAlbumOwner, prependAccountSlug } from '../../../util/account';
import { AccountForWebGalleries } from '../graphql/queries/account';
import { AlbumForWebGalleries } from '../graphql/queries/album';
import { HierarchyAuthTokenVerify } from '../graphql/queries/hierarchy-item';
import { getAccessAuthToken } from '../services/password-store/passwordStore';

const WithWebGalleryAlbumPermissionsHOC = WrappedComponent =>
  class WebGalleryAlbumPermissions extends Component {
    static propTypes = {
      user: PropTypes.shape({
        email_verified: PropTypes.bool.isRequired,
        email_address: PropTypes.string,
      }),
      loadingUserData: PropTypes.bool,
      album: PropTypes.shape({
        is_web_gallery: PropTypes.bool,
        is_private_web_gallery: PropTypes.bool,
        has_password: PropTypes.bool,
        invite_only_access: PropTypes.bool,
        contributor_access: PropTypes.shape({
          view_access: PropTypes.string,
        }),
      }),
      loadingAlbum: PropTypes.bool,
      authenticated: PropTypes.bool.isRequired,
      staticContext: PropTypes.object,
      location: PropTypes.object,
      hasValidPasswordToken: PropTypes.bool,
      loadingPasswordTokenVerify: PropTypes.bool,
    };

    static defaultProps = {
      album: {},
    };

    render() {
      const {
        loadingAlbum,
        loadingUserData,
        user,
        album,
        authenticated,
        staticContext,
        location,
        hasValidPasswordToken,
        loadingPasswordTokenVerify,
      } = this.props;

      if ((!loadingAlbum && !album) || (album.id && !album.is_web_gallery)) {
        return <Redirect to="/not-found" />;
      }

      if (!loadingAlbum && requiresAuthentication(album, authenticated)) {
        if (staticContext) {
          staticContext.referrer = location;
          staticContext.status = 401;
        }

        return <Redirect to="/auth/phone" />;
      }

      // Skip all subsequent permission checks (since ownership trumps them all) and return early.
      if (isAlbumOwner(user, album)) {
        return <WrappedComponent {...this.props} />;
      }

      // For registered users just via email/password
      // check if they have their emails verified
      if (
        !loadingAlbum &&
        !loadingUserData &&
        user &&
        requiresEmailConfirmation(album, user, authenticated)
      ) {
        return (
          <Redirect
            to={{
              pathname: '/auth/email/request-verification',
              state: {
                emailConfirmUrlRedirect: prependAccountSlug(
                  `/galleries/view/album/${album.id}`,
                  album,
                ),
              },
            }}
          />
        );
      }

      // Invite only access
      //
      // Only contributors with view access can view.
      if (
        !loadingAlbum &&
        webGalleryIsInviteOnly(album) &&
        !hasWebGalleryViewAccess(album)
      ) {
        return <Redirect to={`/galleries/no-access/${album.id}`} />;
      }

      // Password authorization check.
      //
      // If the Web Gallery has a password and the user is not password authorized
      // OR not a contributor (contributors get a free pass) redirect to
      // the password screen.
      if (
        !hasWebGalleryContributorAccess(album) &&
        webGalleryHasPassword(album) &&
        !loadingPasswordTokenVerify &&
        !hasValidPasswordToken
      ) {
        if (staticContext) {
          staticContext.referrer = location;
          staticContext.status = 401;
        }

        return (
          <Redirect
            to={{
              pathname: `/galleries/password/${album.id}`,
              search: `?${qs.stringify({
                next: location.pathname,
              })}`,
            }}
          />
        );
      }

      return <WrappedComponent {...this.props} />;
    }
  };

const RequireWebGalleryAlbumPermissions = WrappedComponent => {
  const WithWebGalleryAlbumPermissions =
    WithWebGalleryAlbumPermissionsHOC(WrappedComponent);

  const withUserData = graphql(AccountForWebGalleries, {
    props: ({ data: { me, loading } = {} }) => ({
      user: me,
      loadingUserData: loading,
    }),
    skip: ({ authenticated }) => !authenticated,
  });

  const withAlbum = graphql(AlbumForWebGalleries, {
    options: ({ match }) => ({
      variables: {
        id: match.params.albumId,
      },
    }),
    props: ({ data: { loading, album } = {} }) => ({
      loadingAlbum: loading,
      album,
    }),
    skip: ({ match }) => !match.params.albumId,
  });

  const withHierarchyAuthTokenVerify = graphql(HierarchyAuthTokenVerify, {
    options: ({ album }) => {
      const rootId = getWebGalleryRootId(album);
      const token = getAccessAuthToken(rootId);

      return {
        variables: {
          token,
        },
      };
    },
    props: ({ data: { hierarchyAuthTokenVerify, loading } = {} }) => ({
      hasValidPasswordToken: hierarchyAuthTokenVerify?.is_valid,
      loadingPasswordTokenVerify: loading,
    }),
    skip: ({ album }) => {
      const rootId = getWebGalleryRootId(album);
      const token = getAccessAuthToken(rootId);

      return !album || !webGalleryHasPassword(album) || !token;
    },
  });

  const mapStateToProps = ({ auth }) => ({
    authenticated: auth.authenticated,
  });

  return compose(
    withRouter,
    connect(mapStateToProps),
    withUserData,
    withAlbum,
    withHierarchyAuthTokenVerify,
  )(WithWebGalleryAlbumPermissions);
};

export { WithWebGalleryAlbumPermissionsHOC };
export default RequireWebGalleryAlbumPermissions;
